import { useEffect, useRef } from 'react'
import { Sparkles } from 'lucide-react'
import type { SkillInstalled } from '@/features/skills/skills-api'
import { cn } from '@/lib/utils'

interface SlashPopoverProps {
  query: string
  skills: SkillInstalled[]
  activeIndex: number
  onSelect: (skill: SkillInstalled) => void
  onActiveIndexChange: (i: number) => void
}

export function fuzzyFilter(skills: SkillInstalled[], query: string): SkillInstalled[] {
  const q = query.trim().toLowerCase()
  if (!q) return skills
  return skills.filter((s) => s.skill_name.toLowerCase().includes(q))
}

export function SlashPopover({
  query,
  skills,
  activeIndex,
  onSelect,
  onActiveIndexChange,
}: SlashPopoverProps) {
  const filtered = fuzzyFilter(skills, query)
  const listRef = useRef<HTMLUListElement>(null)

  // Aktiven Eintrag ins Sichtfeld scrollen
  useEffect(() => {
    const list = listRef.current
    if (!list) return
    const item = list.children[activeIndex] as HTMLElement | undefined
    item?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  // Aktiven Index zuruecksetzen wenn die Liste sich aendert und der Index out-of-bounds liegt
  useEffect(() => {
    if (activeIndex >= filtered.length && filtered.length > 0) {
      onActiveIndexChange(0)
    }
  }, [filtered.length, activeIndex, onActiveIndexChange])

  if (filtered.length === 0) {
    return (
      <div className="absolute bottom-full left-0 right-0 mb-2 rounded-md border border-border bg-popover shadow-md">
        <div className="px-4 py-3 text-small text-muted-foreground">
          Keine Skills gefunden{query ? ` für "${query}"` : ''}.
        </div>
      </div>
    )
  }

  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 max-h-64 overflow-hidden rounded-md border border-border bg-popover shadow-md">
      <ul ref={listRef} className="max-h-64 overflow-y-auto py-1">
        {filtered.map((skill, idx) => (
          <li key={skill.id}>
            <button
              type="button"
              onMouseEnter={() => onActiveIndexChange(idx)}
              onClick={() => onSelect(skill)}
              className={cn(
                'flex w-full items-start gap-2 px-3 py-2 text-left transition-colors',
                idx === activeIndex
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-muted',
              )}
            >
              <Sparkles
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0',
                  idx === activeIndex ? 'text-accent-foreground' : 'text-muted-foreground',
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate font-mono text-sm">/{skill.skill_name}</div>
                {skill.description && (
                  <div
                    className={cn(
                      'mt-0.5 line-clamp-1 text-[12px]',
                      idx === activeIndex
                        ? 'text-accent-foreground/80'
                        : 'text-muted-foreground',
                    )}
                  >
                    {skill.description}
                  </div>
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>
      <div className="border-t border-border bg-muted/50 px-3 py-1.5 text-[11px] text-muted-foreground">
        ↑↓ navigieren · Tab/Enter wählen · Esc schliessen
      </div>
    </div>
  )
}
